// export default AdminCSR;
import React, { useEffect, useRef, useState } from "react";
import {
  getCommittee,
  addCommitteeMember,
  updateCommitteeMember,
  deleteCommitteeMember,
  getCsrProjects,
  addCsrProject,
  updateCsrProject,
  deleteCsrProject,
} from "../csrApi";

const CATEGORIES = ["Education", "Health", "Environment", "Livelihood"];

// Backend serves uploaded photos from this origin (port 8000 by default).
const MEDIA_BASE = process.env.REACT_APP_API_BASE_URL;
const mediaUrl = (path) => (path?.startsWith("http") ? path : `${MEDIA_BASE}${path}`);

const emptyMemberForm = { name: "", position: "", photo: null };
const emptyProjectForm = {
  category: CATEGORIES[0],
  title: "",
  description: "",
  photo: null,
};

// ---- Image validation ----
const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
const MAX_IMAGE_SIZE_MB = 20; // keep in sync with backend's multer limits (20MB)

const validateImage = (file) => {
  if (!file) return null;
  if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
    return "This file type isn't supported. Please upload a JPG, PNG or WEBP image.";
  }
  if (file.size > MAX_IMAGE_SIZE_MB * 1024 * 1024) {
    return `Image is too large. Please upload a photo under ${MAX_IMAGE_SIZE_MB}MB.`;
  }
  return null;
};

const AdminCSR = () => {
  const [tab, setTab] = useState("projects"); // "projects" | "committee"

  // ---------- Committee state ----------
  const [members, setMembers] = useState([]);
  const [memberForm, setMemberForm] = useState(emptyMemberForm);
  const [editingMemberId, setEditingMemberId] = useState(null);
  const [memberPhotoError, setMemberPhotoError] = useState("");
  const [memberFormError, setMemberFormError] = useState("");
  const [memberFileVersion, setMemberFileVersion] = useState(0);
  const [savingMember, setSavingMember] = useState(false);
  const memberFormRef = useRef(null);

  // ---------- CSR Project state ----------
  const [projects, setProjects] = useState([]);
  const [projectForm, setProjectForm] = useState(emptyProjectForm);
  const [editingProjectId, setEditingProjectId] = useState(null);
  const [projectPhotoError, setProjectPhotoError] = useState("");
  const [projectFormError, setProjectFormError] = useState("");
  const [projectFileVersion, setProjectFileVersion] = useState(0);
  const [savingProject, setSavingProject] = useState(false);
  const projectFormRef = useRef(null);

  // Brief success confirmation shown for both Committee and Project forms
  const [toast, setToast] = useState("");
  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(""), 3000);
  };

  const loadMembers = () => getCommittee().then((res) => setMembers(res.data));
  const loadProjects = () =>
    getCsrProjects().then((res) => setProjects(res.data));

  useEffect(() => {
    loadMembers();
    loadProjects();
  }, []);

  const scrollToForm = (ref) => {
    setTimeout(() => {
      ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  };

  // ---------------- Committee handlers ----------------
  const handleMemberPhotoChange = (e) => {
    const selected = e.target.files[0] || null;
    const err = validateImage(selected);
    if (err) {
      setMemberPhotoError(err);
      e.target.value = "";
      setMemberForm((f) => ({ ...f, photo: null }));
      return;
    }
    setMemberPhotoError("");
    setMemberForm((f) => ({ ...f, photo: selected }));
  };

  const submitMember = async (e) => {
    e.preventDefault();
    setMemberFormError("");

    if (!memberForm.name || (!memberForm.photo && !editingMemberId)) {
      setMemberFormError("Name and photo are required.");
      return;
    }
    const fd = new FormData();
    fd.append("name", memberForm.name);
    fd.append("position", memberForm.position);
    if (memberForm.photo) fd.append("photo", memberForm.photo);

    try {
      setSavingMember(true);
      if (editingMemberId) {
        await updateCommitteeMember(editingMemberId, fd);
        showToast("Committee member updated successfully!");
      } else {
        await addCommitteeMember(fd);
        showToast("Committee member saved successfully!");
      }
      setMemberForm(emptyMemberForm);
      setEditingMemberId(null);
      setMemberPhotoError("");
      setMemberFileVersion((v) => v + 1);
      await loadMembers();
    } catch (err) {
      const serverMessage = err?.response?.data?.message || err?.response?.data?.error;
      setMemberFormError(
        serverMessage ||
          "We couldn't save this committee member. Please check the photo and try again."
      );
    } finally {
      setSavingMember(false);
    }
  };

  const editMember = (m) => {
    setEditingMemberId(m.id);
    setMemberForm({ name: m.name, position: m.position || "", photo: null });
    setMemberPhotoError("");
    setMemberFormError("");
    setMemberFileVersion((v) => v + 1); // clear any stale "Choose file" selection
    scrollToForm(memberFormRef);
  };

  const cancelMemberEdit = () => {
    setEditingMemberId(null);
    setMemberForm(emptyMemberForm);
    setMemberPhotoError("");
    setMemberFormError("");
    setMemberFileVersion((v) => v + 1);
  };

  const removeMember = async (id) => {
    if (!window.confirm("Delete this committee member?")) return;
    await deleteCommitteeMember(id);
    await loadMembers();
  };

  const currentlyEditingMember = members.find((m) => m.id === editingMemberId);

  // ---------------- CSR Project handlers ----------------
  const handleProjectPhotoChange = (e) => {
    const selected = e.target.files[0] || null;
    const err = validateImage(selected);
    if (err) {
      setProjectPhotoError(err);
      e.target.value = "";
      setProjectForm((f) => ({ ...f, photo: null }));
      return;
    }
    setProjectPhotoError("");
    setProjectForm((f) => ({ ...f, photo: selected }));
  };

  const submitProject = async (e) => {
    e.preventDefault();
    setProjectFormError("");

    if (
      !projectForm.category ||
      !projectForm.title ||
      (!projectForm.photo && !editingProjectId)
    ) {
      setProjectFormError("Category, title and photo are required.");
      return;
    }
    const fd = new FormData();
    fd.append("category", projectForm.category);
    fd.append("title", projectForm.title);
    fd.append("description", projectForm.description);
    if (projectForm.photo) fd.append("photo", projectForm.photo);

    try {
      setSavingProject(true);
      if (editingProjectId) {
        await updateCsrProject(editingProjectId, fd);
        showToast("CSR project updated successfully!");
      } else {
        await addCsrProject(fd);
        showToast("CSR project saved successfully!");
      }
      setProjectForm(emptyProjectForm);
      setEditingProjectId(null);
      setProjectPhotoError("");
      setProjectFileVersion((v) => v + 1);
      await loadProjects();
    } catch (err) {
      const serverMessage = err?.response?.data?.message || err?.response?.data?.error;
      setProjectFormError(
        serverMessage ||
          "We couldn't save this CSR project. Please check the photo and try again."
      );
    } finally {
      setSavingProject(false);
    }
  };

  const editProject = (p) => {
    setEditingProjectId(p.id);
    setProjectForm({
      category: p.category,
      title: p.title,
      description: p.description || "",
      photo: null,
    });
    setProjectPhotoError("");
    setProjectFormError("");
    setProjectFileVersion((v) => v + 1);
    scrollToForm(projectFormRef);
  };

  const cancelProjectEdit = () => {
    setEditingProjectId(null);
    setProjectForm(emptyProjectForm);
    setProjectPhotoError("");
    setProjectFormError("");
    setProjectFileVersion((v) => v + 1);
  };

  const removeProject = async (id) => {
    if (!window.confirm("Delete this CSR project?")) return;
    await deleteCsrProject(id);
    await loadProjects();
  };

  const currentlyEditingProject = projects.find((p) => p.id === editingProjectId);

  const SpinnerIcon = () => (
    <svg className="animate-spin h-4 w-4 text-white" viewBox="0 0 24 24" fill="none">
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
      />
    </svg>
  );

  return (
    <div className="max-w-5xl mx-auto p-4">
      <h1 className="text-2xl font-bold mb-6">CSR Management</h1>

      {toast && (
        <div className="fixed top-4 right-4 z-50 bg-green-600 text-white px-4 py-2 rounded shadow-lg text-sm font-medium">
          ✓ {toast}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 mb-8 border-b">
        <button
          onClick={() => setTab("projects")}
          className={`px-6 py-2 font-semibold rounded-t ${
            tab === "projects"
              ? "bg-orange-300 text-gray-900"
              : "bg-gray-100 text-gray-500 hover:bg-gray-200"
          }`}
        >
          CSR Project
        </button>
        <button
          onClick={() => setTab("committee")}
          className={`px-6 py-2 font-semibold rounded-t ${
            tab === "committee"
              ? "bg-orange-300 text-gray-900"
              : "bg-gray-100 text-gray-500 hover:bg-gray-200"
          }`}
        >
          Committee
        </button>
      </div>

      {/* ---------------- CSR Project tab ---------------- */}
      {tab === "projects" && (
        <div>
          <form
            ref={projectFormRef}
            onSubmit={submitProject}
            className="bg-white rounded shadow p-4 mb-8 grid gap-3 sm:grid-cols-2"
          >
            <h2 className="sm:col-span-2 font-semibold text-lg">
              {editingProjectId ? "Edit CSR Project" : "Add CSR Project"}
            </h2>

            {projectFormError && (
              <div className="sm:col-span-2 bg-red-50 border border-red-200 text-red-700 text-sm rounded p-2">
                {projectFormError}
              </div>
            )}

            <select
              value={projectForm.category}
              onChange={(e) =>
                setProjectForm({ ...projectForm, category: e.target.value })
              }
              className="border rounded p-2"
            >
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>

            <input
              type="text"
              placeholder="Title"
              value={projectForm.title}
              onChange={(e) =>
                setProjectForm({ ...projectForm, title: e.target.value })
              }
              className="border rounded p-2"
            />

            <textarea
              placeholder="Description (optional)"
              value={projectForm.description}
              onChange={(e) =>
                setProjectForm({
                  ...projectForm,
                  description: e.target.value,
                })
              }
              className="border rounded p-2 sm:col-span-2"
              rows={2}
            />

            <div className="sm:col-span-2">
              {editingProjectId && currentlyEditingProject?.photo && !projectForm.photo && (
                <div className="flex items-center gap-2 mb-2">
                  <img
                    src={mediaUrl(currentlyEditingProject.photo)}
                    alt="Current"
                    className="w-12 h-12 object-cover rounded border"
                  />
                  <span className="text-xs text-gray-500">
                    Current photo (choose a new file below to replace it)
                  </span>
                </div>
              )}
              <input
                key={`project-photo-${projectFileVersion}`}
                type="file"
                accept="image/png, image/jpeg, image/webp"
                onChange={handleProjectPhotoChange}
              />
              {projectPhotoError && (
                <p className="text-xs text-red-600 mt-1">{projectPhotoError}</p>
              )}
            </div>

            <div className="sm:col-span-2 flex gap-2">
              <button
                type="submit"
                disabled={savingProject}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded disabled:opacity-60 flex items-center gap-2"
              >
                {savingProject && <SpinnerIcon />}
                {savingProject ? "Saving..." : editingProjectId ? "Update" : "Add"}
              </button>
              {editingProjectId && (
                <button
                  type="button"
                  onClick={cancelProjectEdit}
                  disabled={savingProject}
                  className="bg-gray-300 hover:bg-gray-400 px-4 py-2 rounded disabled:opacity-60"
                >
                  Cancel
                </button>
              )}
            </div>
          </form>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {projects.map((p) => (
              <div key={p.id} className="bg-white rounded shadow overflow-hidden">
                <img src={mediaUrl(p.photo)} alt={p.title} className="w-full h-32 object-cover" />
                <div className="p-3">
                  <p className="text-xs text-orange-600 font-semibold">{p.category}</p>
                  <p className="font-semibold">{p.title}</p>
                  {p.description && (
                    <p className="text-sm text-gray-500">{p.description}</p>
                  )}
                  <div className="flex gap-2 mt-2">
                    <button
                      onClick={() => editProject(p)}
                      className="text-sm bg-yellow-500 hover:bg-yellow-600 text-white px-3 py-1 rounded"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => removeProject(p.id)}
                      className="text-sm bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ---------------- Committee tab ---------------- */}
      {tab === "committee" && (
        <div>
          <form
            ref={memberFormRef}
            onSubmit={submitMember}
            className="bg-white rounded shadow p-4 mb-8 grid gap-3 sm:grid-cols-2"
          >
            <h2 className="sm:col-span-2 font-semibold text-lg">
              {editingMemberId ? "Edit Committee Member" : "Add Committee Member"}
            </h2>

            {memberFormError && (
              <div className="sm:col-span-2 bg-red-50 border border-red-200 text-red-700 text-sm rounded p-2">
                {memberFormError}
              </div>
            )}

            <input
              type="text"
              placeholder="Name"
              value={memberForm.name}
              onChange={(e) =>
                setMemberForm({ ...memberForm, name: e.target.value })
              }
              maxLength={60}
              className="border rounded p-2"
            />

            <input
              type="text"
              placeholder="Position (optional)"
              value={memberForm.position}
              onChange={(e) =>
                setMemberForm({ ...memberForm, position: e.target.value })
              }
              maxLength={80}
              className="border rounded p-2"
            />

            <div className="sm:col-span-2">
              {editingMemberId && currentlyEditingMember?.photo && !memberForm.photo && (
                <div className="flex items-center gap-2 mb-2">
                  <img
                    src={mediaUrl(currentlyEditingMember.photo)}
                    alt="Current"
                    className="w-12 h-12 object-cover rounded-full border"
                  />
                  <span className="text-xs text-gray-500">
                    Current photo (choose a new file below to replace it)
                  </span>
                </div>
              )}
              <input
                key={`member-photo-${memberFileVersion}`}
                type="file"
                accept="image/png, image/jpeg, image/webp"
                onChange={handleMemberPhotoChange}
              />
              {memberPhotoError && (
                <p className="text-xs text-red-600 mt-1">{memberPhotoError}</p>
              )}
            </div>

            <div className="sm:col-span-2 flex gap-2">
              <button
                type="submit"
                disabled={savingMember}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded disabled:opacity-60 flex items-center gap-2"
              >
                {savingMember && <SpinnerIcon />}
                {savingMember ? "Saving..." : editingMemberId ? "Update" : "Add"}
              </button>
              {editingMemberId && (
                <button
                  type="button"
                  onClick={cancelMemberEdit}
                  disabled={savingMember}
                  className="bg-gray-300 hover:bg-gray-400 px-4 py-2 rounded disabled:opacity-60"
                >
                  Cancel
                </button>
              )}
            </div>
          </form>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
            {members.map((m) => (
              <div key={m.id} className="bg-white rounded shadow p-3 text-center">
                <img
                  src={mediaUrl(m.photo)}
                  alt={m.name}
                  className="w-20 h-20 object-cover rounded-full mx-auto mb-2 border"
                />
                <p
                  className="font-semibold break-words line-clamp-2"
                  title={m.name}
                >
                  {m.name}
                </p>
                {m.position && (
                  <p
                    className="text-sm text-gray-500 break-words line-clamp-2"
                    title={m.position}
                  >
                    {m.position}
                  </p>
                )}
                <div className="flex justify-center gap-2 mt-2">
                  <button
                    onClick={() => editMember(m)}
                    className="text-sm bg-yellow-500 hover:bg-yellow-600 text-white px-3 py-1 rounded"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => removeMember(m.id)}
                    className="text-sm bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCSR;